import React from "react";
import AlertModal from "./AlertModal";
import { useSessionActivity } from "../../core/hooks/useSessionActivity";
import { useAuth } from "../../core/hooks/useAuth";
import { useRouter } from "../../core/hooks/useRouter";
import {
  logoutText,
  sessionTimeoutBodyText,
  sessionTimeoutTitleText,
  stayLoggedInText,
} from "../../core/constants/strings";

function formatCountdown(secondsRemaining: number): string {
  const minutes = Math.floor(secondsRemaining / 60);
  const seconds = secondsRemaining % 60;
  return `${minutes}:${seconds.toString().padStart(2, "0")}`;
}

/**
 * Thin wrapper over the existing AlertModal pattern — the same one Header
 * uses for its logout confirmation — so no new modal markup is introduced.
 * AlertModal already portals to document.body, which is what stops an
 * ancestor's overflow rule swallowing clicks (PPI-1391).
 *
 * useSessionActivity supplies the one thing AlertModal cannot: a countdown
 * body that changes on every poll tick while the warning is visible.
 *
 * Mounted last in App's Router subtree, deliberately after Footer.
 * AttestationModal is non-dismissable and can sit open indefinitely on an
 * unattended tab; mounting after it means this paints on top if both are
 * open. Note that the idle *sign-out* is never suppressed by attestation
 * being open — an unattended tab must still time out for the control to mean
 * anything. Only this dialog's visibility yields.
 */
const SessionIdleWarningModal = () => {
  const auth = useAuth();
  const router = useRouter();
  const {
    isWarningVisible,
    secondsRemaining,
    extendSession,
  } = useSessionActivity();

  function logoutNow() {
    auth.logout();
    router.replace("/login");
  }

  return (
    <AlertModal
      isOpen={isWarningVisible}
      title={sessionTimeoutTitleText}
      body={sessionTimeoutBodyText.replace(
        "{countdown}",
        formatCountdown(secondsRemaining)
      )}
      positive={stayLoggedInText}
      negative={logoutText}
      onPositive={extendSession}
      onNegative={logoutNow}
      onDismiss={extendSession}
    />
  );
};

export default SessionIdleWarningModal;
